import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import {
  Users,
  TrendingUp,
  TrendingDown,
  Activity,
  AlertTriangle,
  Mic2,
  ChevronDown,
  ChevronRight,
  ShieldAlert,
  Loader2,
  ArrowRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type RangeKey = "week" | "month";

interface MeResponse {
  isManager: boolean;
  role: string;
  departmentId: string;
  canSeeTeam: boolean;
}

interface TeamRep {
  userId: string;
  fullName: string;
  email: string | null;
  role: string;
  departmentId: string;
  isManager: boolean;
}

interface TeamResponse {
  manager: { userId: string; departmentId: string; role: string };
  reps: TeamRep[];
}

interface StageBucket {
  count: number;
  weightedValue: number;
}

interface PipelineRow {
  userId: string;
  fullName: string;
  departmentId: string;
  role: string;
  leadsCreated: number;
  clientsCreated: number;
  openLeads: number;
  dollarsBooked: number;
  weightedPipelineValue: number;
  invoiceCount: number;
  byStage: Record<string, StageBucket>;
}

interface PipelineResponse {
  range: RangeKey;
  totals: {
    leadsCreated: number;
    clientsCreated: number;
    openLeads: number;
    dollarsBooked: number;
    weightedPipelineValue: number;
    invoiceCount: number;
  };
  rows: PipelineRow[];
}

interface ActivityRow {
  userId: string;
  fullName: string;
  calls: number;
  emails: number;
  notes: number;
  smsSent: number;
  meetingsBooked: number;
  leadsAdded: number;
  total: number;
  sparkline: number[];
}

interface ActivityResponse {
  range: RangeKey;
  totals: {
    calls: number;
    emails: number;
    notes: number;
    smsSent: number;
    meetingsBooked: number;
    leadsAdded: number;
    total: number;
  };
  rows: ActivityRow[];
}

interface PaceMetric {
  count: number;
  goal: number;
  pct: number;
  paceStatus: "ahead" | "on_track" | "behind";
}

interface PacingRow {
  userId: string;
  fullName: string;
  activity?: PaceMetric;
  pipeline?: PaceMetric;
  wins?: PaceMetric;
  error?: string;
}

interface PacingResponse {
  range: RangeKey;
  rows: PacingRow[];
}

interface ConflictRow {
  id: string;
  kind: string;
  severity: string;
  date: string;
  summary: string;
  status: string;
}

interface TalentUtilRow {
  talentId: string;
  name: string;
  invoiceCount: number;
  dollarsBooked: number;
  upcomingShowsNext30Days: number;
}

interface AtRiskInvoice {
  invoiceId: string;
  invoiceNumber: string | null;
  accountName: string | null;
  status: string;
  dollars: number;
  dateOfEvent: string | null;
  lastTouchedAt: string | null;
  daysSinceTouch: number | null;
  repId: string;
  repName: string;
}

interface AtRiskResponse {
  totalAtRiskDollars: number;
  totalAtRiskCount: number;
  rows: AtRiskInvoice[];
}

interface ResponseSlaRow {
  userId: string;
  fullName: string;
  leadCount: number;
  touchedCount: number;
  untouchedCount: number;
  medianMinutesToFirstTouch: number | null;
  p90MinutesToFirstTouch: number | null;
}

interface ResponseSlaResponse {
  windowDays: number;
  rows: ResponseSlaRow[];
}

interface PipelineTrendRow {
  userId: string;
  priorDollarsBooked: number;
}

interface PipelineTrendResponse {
  range: RangeKey;
  priorPeriod: { start: string; end: string };
  rows: PipelineTrendRow[];
}

interface RepSliceItem {
  id: string;
  name: string;
  status?: string;
  daysSinceContact?: number | null;
  bookedAt?: string | null;
}

interface RepSliceResponse {
  userId: string;
  stuck: RepSliceItem[];
  slipping: RepSliceItem[];
  closed: RepSliceItem[];
  counts: { stuck: number; slipping: number; closed: number };
}

function formatMinutes(mins: number): string {
  if (mins < 60) return `${mins}m`;
  const hours = mins / 60;
  if (hours < 24) return `${hours.toFixed(hours < 10 ? 1 : 0)}h`;
  const days = hours / 24;
  return `${days.toFixed(days < 10 ? 1 : 0)}d`;
}

function formatMoney(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

function PaceBadge({ status }: { status: PaceMetric["paceStatus"] }) {
  if (status === "ahead") {
    return (
      <Badge className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-900">
        <TrendingUp className="h-3 w-3 mr-1" /> Ahead
      </Badge>
    );
  }
  if (status === "behind") {
    return (
      <Badge className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-900">
        <TrendingDown className="h-3 w-3 mr-1" /> Behind
      </Badge>
    );
  }
  return (
    <Badge variant="secondary">
      <Activity className="h-3 w-3 mr-1" /> On track
    </Badge>
  );
}

function Sparkline({ values }: { values: number[] }) {
  const max = Math.max(1, ...values);
  return (
    <div className="flex items-end gap-0.5 h-6" data-testid="activity-sparkline">
      {values.map((v, i) => (
        <div
          key={i}
          className="w-1.5 bg-primary/60 rounded-sm"
          style={{ height: `${Math.max(2, (v / max) * 24)}px` }}
        />
      ))}
    </div>
  );
}

function NotAuthorized() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-8">
      <ShieldAlert className="w-16 h-16 text-destructive mb-4" />
      <h1 className="text-2xl font-bold mb-2">Manager access required</h1>
      <p className="text-muted-foreground text-center max-w-md">
        The Team dashboard is only visible to people flagged as a manager in
        their workspace profile. Ask an admin to enable it for you.
      </p>
      <Link href="/">
        <Button className="mt-6" data-testid="button-team-go-home">
          Back to Dashboard
        </Button>
      </Link>
    </div>
  );
}

function RepSliceDrillDown({
  userId,
  range,
  fullName,
}: {
  userId: string;
  range: RangeKey;
  fullName: string;
}) {
  const tzOffset = new Date().getTimezoneOffset();
  const { data, isLoading } = useQuery<RepSliceResponse>({
    queryKey: ["/api/manager-dashboard/rep", userId, "slice", range, tzOffset],
    queryFn: async () => {
      const res = await fetch(
        `/api/manager-dashboard/rep/${encodeURIComponent(userId)}/slice?range=${range}&tzOffset=${tzOffset}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error("Failed to load rep slice");
      return res.json();
    },
  });

  if (isLoading || !data) {
    return <Skeleton className="h-24 w-full" data-testid={`skeleton-slice-${userId}`} />;
  }

  const Section = ({
    label,
    items,
    testId,
    tone,
  }: {
    label: string;
    items: RepSliceItem[];
    testId: string;
    tone: "danger" | "warn" | "ok";
  }) => (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs uppercase text-muted-foreground">
          {label}
        </div>
        <Badge
          variant={tone === "danger" ? "destructive" : tone === "warn" ? "secondary" : "outline"}
          className="text-xs"
        >
          {items.length}
        </Badge>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-muted-foreground italic">None</div>
      ) : (
        <ul className="text-sm space-y-0.5 max-h-32 overflow-y-auto">
          {items.slice(0, 8).map((item) => (
            <li key={item.id} className="truncate" title={item.name}>
              {item.name}
              {item.daysSinceContact !== undefined && item.daysSinceContact !== null && (
                <span className="text-xs text-muted-foreground ml-1">
                  · {item.daysSinceContact}d
                </span>
              )}
            </li>
          ))}
          {items.length > 8 && (
            <li className="text-xs text-muted-foreground">
              +{items.length - 8} more
            </li>
          )}
        </ul>
      )}
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Section label="Stuck (no contact 14d+)" items={data.stuck} testId={`slice-stuck-${userId}`} tone="danger" />
      <Section label="Slipping (interested, 7d+)" items={data.slipping} testId={`slice-slipping-${userId}`} tone="warn" />
      <Section label={`Closed (${range})`} items={data.closed} testId={`slice-closed-${userId}`} tone="ok" />
      <div className="md:col-span-3 flex flex-wrap gap-2">
        <Link href={`/leads?team=mine&rep=${encodeURIComponent(fullName)}`}>
          <Button size="sm" variant="outline" data-testid={`button-rep-leads-${userId}`}>
            View leads <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </Link>
        <Link href={`/clients?team=mine&rep=${encodeURIComponent(fullName)}`}>
          <Button size="sm" variant="outline" data-testid={`button-rep-clients-${userId}`}>
            View bookings <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default function ManagerDashboardPage() {
  const [range, setRange] = useState<RangeKey>("week");
  const [expandedRepId, setExpandedRepId] = useState<string | null>(null);
  const tzOffset = new Date().getTimezoneOffset();

  const { data: me, isLoading: meLoading } = useQuery<MeResponse>({
    queryKey: ["/api/manager-dashboard/me"],
  });

  const enabled = !!me?.canSeeTeam;
  const dept = me?.departmentId || "ops";
  // Department-aware card visibility:
  // - billing: hides leads/pipeline columns; cares about $ + invoices
  // - marketing: focuses on top-of-funnel (campaign activity, leads-in,
  //   pipeline-from-marketing-leads); hides $ won column
  // - sales/ops: full view
  // - talent: full view + talent utilization rollup
  const showLeadsColumn = dept !== "billing";
  const showPipelineCard = dept !== "billing";
  const showDollarsCard = dept !== "marketing";
  const isTalentDept = dept === "talent";
  const isMarketingDept = dept === "marketing";
  const isBillingDept = dept === "billing";
  // Team-prefiltered deep-link query so /leads, /clients, /calendars,
  // /talent open scoped to the manager's reports. The shared
  // `team=mine` token is the agreed-upon convention from #702 — the
  // existing list pages already understand it (and degrade to "show
  // mine" when the param is missing).
  const teamQS = "team=mine";

  const { data: team, isLoading: teamLoading } = useQuery<TeamResponse>({
    queryKey: ["/api/manager-dashboard/team"],
    enabled,
  });

  const { data: pipeline, isLoading: pipelineLoading } = useQuery<PipelineResponse>({
    queryKey: ["/api/manager-dashboard/pipeline", range, tzOffset],
    queryFn: async () => {
      const res = await fetch(
        `/api/manager-dashboard/pipeline?range=${range}&tzOffset=${tzOffset}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error("Failed to load pipeline");
      return res.json();
    },
    enabled,
  });

  const { data: activity } = useQuery<ActivityResponse>({
    queryKey: ["/api/manager-dashboard/activity", range, tzOffset],
    queryFn: async () => {
      const res = await fetch(
        `/api/manager-dashboard/activity?range=${range}&tzOffset=${tzOffset}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error("Failed to load activity");
      return res.json();
    },
    enabled,
  });

  const { data: pacing } = useQuery<PacingResponse>({
    queryKey: ["/api/manager-dashboard/pacing", range],
    queryFn: async () => {
      const res = await fetch(`/api/manager-dashboard/pacing?range=${range}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load pacing");
      return res.json();
    },
    enabled,
  });

  const { data: conflicts } = useQuery<{ rows: ConflictRow[] }>({
    queryKey: ["/api/manager-dashboard/conflicts"],
    enabled,
  });

  const { data: talentUtil } = useQuery<{ rows: TalentUtilRow[] }>({
    queryKey: ["/api/manager-dashboard/talent-utilization", range, tzOffset],
    queryFn: async () => {
      const res = await fetch(
        `/api/manager-dashboard/talent-utilization?range=${range}&tzOffset=${tzOffset}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error("Failed to load talent utilization");
      return res.json();
    },
    enabled: enabled && isTalentDept,
  });

  const { data: atRisk } = useQuery<AtRiskResponse>({
    queryKey: ["/api/manager-dashboard/at-risk"],
    enabled,
  });

  const { data: sla } = useQuery<ResponseSlaResponse>({
    queryKey: ["/api/manager-dashboard/response-sla"],
    enabled,
  });

  const { data: trend } = useQuery<PipelineTrendResponse>({
    queryKey: ["/api/manager-dashboard/pipeline/trend", range, tzOffset],
    queryFn: async () => {
      const res = await fetch(
        `/api/manager-dashboard/pipeline/trend?range=${range}&tzOffset=${tzOffset}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error("Failed to load pipeline trend");
      return res.json();
    },
    enabled,
  });

  const trendByRep = useMemo(() => {
    const m = new Map<string, number>();
    (trend?.rows || []).forEach((r) => m.set(r.userId, r.priorDollarsBooked));
    return m;
  }, [trend]);

  const activityByUser = useMemo(() => {
    const m = new Map<string, ActivityRow>();
    (activity?.rows || []).forEach((r) => m.set(r.userId, r));
    return m;
  }, [activity]);

  const pacingByUser = useMemo(() => {
    const m = new Map<string, PacingRow>();
    (pacing?.rows || []).forEach((r) => m.set(r.userId, r));
    return m;
  }, [pacing]);

  if (meLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!me?.canSeeTeam) {
    return <NotAuthorized />;
  }

  const reps = team?.reps || [];

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-7xl space-y-6">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Users className="h-7 w-7" /> Team Dashboard
          </h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="text-dept-tagline">
            {isBillingDept
              ? "Invoice throughput + booked-dollar pacing across your billing team."
              : isMarketingDept
                ? "Campaign activity, leads-in, and pipeline-from-marketing-leads across your marketing team."
                : isTalentDept
                  ? "Bookings, talent utilization, and conflicts across your talent team."
                  : "Rollup across your direct + indirect reports."}{" "}
            <Badge variant="outline" className="ml-1 capitalize" data-testid="badge-dept">
              {dept}
            </Badge>
          </p>
        </div>
        <Select value={range} onValueChange={(v) => setRange(v as RangeKey)}>
          <SelectTrigger className="w-36" data-testid="select-range">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="week">This week</SelectItem>
            <SelectItem value="month">This month</SelectItem>
          </SelectContent>
        </Select>
      </header>

      {teamLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : reps.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground" data-testid="empty-no-reports">
            No direct or indirect reports are assigned to you yet. Ask an admin
            to set up the reporting structure in Users & Permissions.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Department-aware totals row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card data-testid="card-total-team-size">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium text-muted-foreground uppercase">
                  Team size
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{reps.length}</div>
              </CardContent>
            </Card>
            {showLeadsColumn && (
              <Card data-testid="card-total-leads">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase">
                    Leads created
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {pipelineLoading ? <Skeleton className="h-7 w-12" /> : pipeline?.totals.leadsCreated ?? 0}
                  </div>
                </CardContent>
              </Card>
            )}
            {showPipelineCard && (
              <Card data-testid="card-pipeline-value">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase">
                    Weighted pipeline
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {pipelineLoading ? (
                      <Skeleton className="h-7 w-20" />
                    ) : (
                      formatMoney(pipeline?.totals.weightedPipelineValue ?? 0)
                    )}
                  </div>
                  <Link href={`/leads?${teamQS}`}>
                    <Button variant="link" size="sm" className="px-0 h-6 text-xs" data-testid="link-pipeline-leads">
                      View team leads <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
            {showDollarsCard && (
              <Card data-testid="card-total-dollars">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase">
                    Dollars booked
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {pipelineLoading ? (
                      <Skeleton className="h-7 w-20" />
                    ) : (
                      formatMoney(pipeline?.totals.dollarsBooked ?? 0)
                    )}
                  </div>
                  <Link href={`/clients?${teamQS}`}>
                    <Button variant="link" size="sm" className="px-0 h-6 text-xs" data-testid="link-dollars-clients">
                      View team bookings <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
            {/* Billing-only: invoice throughput. */}
            {isBillingDept && (
              <Card data-testid="card-invoice-throughput">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase">
                    Invoices created
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {pipelineLoading ? (
                      <Skeleton className="h-7 w-12" />
                    ) : (
                      pipeline?.totals.invoiceCount ?? 0
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
            {/* Marketing-only: SMS sent + meetings-booked campaign signal. */}
            {isMarketingDept && (
              <Card data-testid="card-marketing-campaign-activity">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase">
                    Campaign activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {activity ? (
                      (activity.totals.smsSent ?? 0) + (activity.totals.emails ?? 0)
                    ) : (
                      <Skeleton className="h-7 w-12" />
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Email + SMS sent across your team
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* At-Risk Pipeline + Response SLA */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card data-testid="card-at-risk">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  At-Risk Pipeline
                  {atRisk && atRisk.totalAtRiskCount > 0 && (
                    <Badge variant="destructive" className="ml-2" data-testid="badge-at-risk-count">
                      {atRisk.totalAtRiskCount}
                    </Badge>
                  )}
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Hold/pending invoices with no touch in 14+ days
                </p>
              </CardHeader>
              <CardContent>
                {!atRisk ? (
                  <Skeleton className="h-24 w-full" />
                ) : atRisk.rows.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic py-3">
                    No stale pipeline. Your team is on top of follow-ups.
                  </p>
                ) : (
                  <>
                    <div className="text-sm text-muted-foreground mb-2">
                      <span className="font-semibold text-foreground" data-testid="text-at-risk-total">
                        {formatMoney(atRisk.totalAtRiskDollars)}
                      </span>{" "}
                      across {atRisk.totalAtRiskCount} invoice{atRisk.totalAtRiskCount === 1 ? "" : "s"}
                    </div>
                    <ul className="divide-y max-h-72 overflow-y-auto">
                      {atRisk.rows.slice(0, 10).map((inv) => (
                        <li key={inv.invoiceId} className="py-2" data-testid={`row-at-risk-${inv.invoiceId}`}>
                          <Link href={`/invoices?focus=${inv.invoiceId}`} className="flex items-center justify-between gap-3 hover:bg-muted/30 -mx-2 px-2 rounded">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium truncate">
                                {inv.accountName ?? `Invoice ${inv.invoiceNumber ?? ""}`}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {inv.repName} · <span className="capitalize">{inv.status}</span> · {inv.daysSinceTouch}d cold
                              </p>
                            </div>
                            <span className="text-sm font-semibold tabular-nums whitespace-nowrap">
                              {formatMoney(inv.dollars)}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </CardContent>
            </Card>

            <Card data-testid="card-response-sla">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Activity className="h-5 w-5 text-primary" />
                  Response SLA
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Median time-to-first-touch on new leads (last 30 days)
                </p>
              </CardHeader>
              <CardContent>
                {!sla ? (
                  <Skeleton className="h-24 w-full" />
                ) : sla.rows.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic py-3">
                    No leads created in the past 30 days.
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left py-1 font-medium">Rep</th>
                        <th className="text-right py-1 font-medium">Median</th>
                        <th className="text-right py-1 font-medium">P90</th>
                        <th className="text-right py-1 font-medium">Untouched</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sla.rows.map((r) => (
                        <tr key={r.userId} className="border-t" data-testid={`row-sla-${r.userId}`}>
                          <td className="py-1.5 truncate max-w-[160px]">{r.fullName}</td>
                          <td className="text-right py-1.5 tabular-nums">
                            {r.medianMinutesToFirstTouch === null ? "—" : formatMinutes(r.medianMinutesToFirstTouch)}
                          </td>
                          <td className="text-right py-1.5 tabular-nums text-muted-foreground">
                            {r.p90MinutesToFirstTouch === null ? "—" : formatMinutes(r.p90MinutesToFirstTouch)}
                          </td>
                          <td className="text-right py-1.5 tabular-nums">
                            {r.untouchedCount > 0 ? (
                              <Badge variant="outline" className="text-amber-600 border-amber-400">
                                {r.untouchedCount}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">0</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Conflict radar slice — always rendered so empty state is explicit. */}
          <Card data-testid="card-conflicts">
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Conflicts on your team
                {conflicts && conflicts.rows.length > 0 && (
                  <Badge variant="destructive" className="ml-2">
                    {conflicts.rows.length}
                  </Badge>
                )}
              </CardTitle>
              <Link href={`/calendars?panel=conflicts&${teamQS}`}>
                <Button variant="link" size="sm" data-testid="link-conflicts-all">
                  View team conflicts <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="space-y-2">
              {!conflicts ? (
                <Skeleton className="h-16 w-full" />
              ) : conflicts.rows.length === 0 ? (
                <div
                  className="text-sm text-muted-foreground italic py-3"
                  data-testid="text-conflicts-empty"
                >
                  No active conflicts for your team. Nice.
                </div>
              ) : (
                conflicts.rows.slice(0, 6).map((c) => (
                  <div
                    key={c.id}
                    className="flex items-start justify-between gap-3 p-2 rounded border bg-card"
                    data-testid={`row-conflict-${c.id}`}
                  >
                    <div className="text-sm">
                      <div className="font-medium">{c.summary}</div>
                      <div className="text-xs text-muted-foreground capitalize">
                        {c.kind.replace(/_/g, " ")} · {c.severity}
                      </div>
                    </div>
                    <Link href={`/calendars?panel=conflicts&${teamQS}`}>
                      <Button size="sm" variant="ghost">Open</Button>
                    </Link>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Per-rep table with drill-down */}
          <Card data-testid="card-rep-table">
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Per-rep breakdown</CardTitle>
              <Link href={`/leads?${teamQS}`}>
                <Button variant="link" size="sm" data-testid="link-reps-all">
                  All team leads <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground border-b bg-muted/40">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Rep</th>
                    {showLeadsColumn && (
                      <th className="text-right px-3 py-2 font-medium">Leads</th>
                    )}
                    {showPipelineCard && (
                      <th className="text-right px-3 py-2 font-medium">Open</th>
                    )}
                    <th className="text-right px-3 py-2 font-medium">Bookings</th>
                    {showDollarsCard && (
                      <th className="text-right px-3 py-2 font-medium">$ booked</th>
                    )}
                    <th className="text-right px-3 py-2 font-medium">Activity</th>
                    <th className="px-3 py-2 font-medium">7d</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {reps.map((rep) => {
                    const row = pipeline?.rows.find((r) => r.userId === rep.userId);
                    const expanded = expandedRepId === rep.userId;
                    const act = activityByUser.get(rep.userId);
                    const pace = pacingByUser.get(rep.userId);
                    const colCount =
                      2 + // Rep + 7d/expander cells
                      (showLeadsColumn ? 1 : 0) +
                      (showPipelineCard ? 1 : 0) +
                      1 + // Bookings
                      (showDollarsCard ? 1 : 0) +
                      1 + // Activity
                      1; // Pace
                    return (
                      <>
                        <tr
                          key={rep.userId}
                          className="border-b hover:bg-muted/30 cursor-pointer"
                          onClick={() =>
                            setExpandedRepId(expanded ? null : rep.userId)
                          }
                          data-testid={`row-rep-${rep.userId}`}
                        >
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              {expanded ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              )}
                              <div>
                                <div className="font-medium">{rep.fullName}</div>
                                <div className="text-xs text-muted-foreground capitalize">
                                  {rep.role.replace(/_/g, " ")} · {rep.departmentId}
                                </div>
                              </div>
                            </div>
                          </td>
                          {showLeadsColumn && (
                            <td className="text-right px-3 py-2 tabular-nums">
                              {row?.leadsCreated ?? 0}
                            </td>
                          )}
                          {showPipelineCard && (
                            <td className="text-right px-3 py-2 tabular-nums">
                              {row?.openLeads ?? 0}
                            </td>
                          )}
                          <td className="text-right px-3 py-2 tabular-nums">
                            {row?.clientsCreated ?? 0}
                          </td>
                          {showDollarsCard && (
                            <td className="text-right px-3 py-2 tabular-nums font-medium">
                              <div className="flex items-center justify-end gap-1.5">
                                <span>{formatMoney(row?.dollarsBooked ?? 0)}</span>
                                {(() => {
                                  const cur = row?.dollarsBooked ?? 0;
                                  const prior = trendByRep.get(rep.userId);
                                  if (prior === undefined) return null;
                                  if (prior === 0 && cur === 0) return null;
                                  const delta = cur - prior;
                                  if (Math.abs(delta) < 1) return null;
                                  const up = delta > 0;
                                  const pct = prior > 0 ? Math.round((delta / prior) * 100) : null;
                                  return (
                                    <span
                                      className={`inline-flex items-center text-xs ${up ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
                                      title={`Prior period: ${formatMoney(prior)}`}
                                      data-testid={`trend-rep-${rep.userId}`}
                                    >
                                      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                                      {pct !== null && <span className="ml-0.5">{pct > 0 ? "+" : ""}{pct}%</span>}
                                    </span>
                                  );
                                })()}
                              </div>
                            </td>
                          )}
                          <td className="text-right px-3 py-2 tabular-nums">
                            {act?.total ?? "—"}
                          </td>
                          <td className="px-3 py-2">
                            {act?.sparkline ? <Sparkline values={act.sparkline} /> : "—"}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {pace?.activity && (
                              <PaceBadge status={pace.activity.paceStatus} />
                            )}
                          </td>
                        </tr>
                        {expanded && (
                          <tr
                            key={`${rep.userId}-detail`}
                            className="border-b bg-muted/20"
                            data-testid={`row-rep-detail-${rep.userId}`}
                          >
                            <td colSpan={colCount} className="px-6 py-4 space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <div className="text-xs uppercase text-muted-foreground mb-1">
                                    Activity ({range})
                                  </div>
                                  {act ? (
                                    <ul className="text-sm space-y-1">
                                      <li>Calls: <span className="font-medium">{act.calls}</span></li>
                                      <li>Emails: <span className="font-medium">{act.emails}</span></li>
                                      <li>SMS sent: <span className="font-medium">{act.smsSent}</span></li>
                                      <li>Meetings booked: <span className="font-medium">{act.meetingsBooked}</span></li>
                                      <li>Notes: <span className="font-medium">{act.notes}</span></li>
                                      <li>Leads added: <span className="font-medium">{act.leadsAdded}</span></li>
                                    </ul>
                                  ) : (
                                    <Skeleton className="h-16 w-full" />
                                  )}
                                </div>
                                <div>
                                  <div className="text-xs uppercase text-muted-foreground mb-1">
                                    Pacing
                                  </div>
                                  {pace?.error ? (
                                    <div className="text-xs text-muted-foreground">
                                      Could not load pacing for this rep.
                                    </div>
                                  ) : pace &&
                                    (pace.activity?.goal ?? 0) === 0 &&
                                    (pace.pipeline?.goal ?? 0) === 0 &&
                                    (pace.wins?.goal ?? 0) === 0 ? (
                                    // Neutral "no quota set" state — we
                                    // don't want to flash red pace badges
                                    // at a rep just because nobody set
                                    // their goals yet.
                                    <div
                                      className="text-xs text-muted-foreground"
                                      data-testid={`text-pacing-no-quota-${rep.userId}`}
                                    >
                                      No quota set — ask an admin to
                                      configure activity, pipeline, and
                                      win goals for this rep in Settings.
                                    </div>
                                  ) : pace ? (
                                    <ul className="text-sm space-y-1">
                                      {pace.activity && (
                                        <li className="flex items-center gap-2">
                                          <span className="w-16 text-muted-foreground">Activity</span>
                                          <span className="font-medium">{pace.activity.count}/{pace.activity.goal}</span>
                                          <PaceBadge status={pace.activity.paceStatus} />
                                        </li>
                                      )}
                                      {pace.pipeline && (
                                        <li className="flex items-center gap-2">
                                          <span className="w-16 text-muted-foreground">Pipeline</span>
                                          <span className="font-medium">{pace.pipeline.count}/{pace.pipeline.goal}</span>
                                          <PaceBadge status={pace.pipeline.paceStatus} />
                                        </li>
                                      )}
                                      {pace.wins && (
                                        <li className="flex items-center gap-2">
                                          <span className="w-16 text-muted-foreground">Wins</span>
                                          <span className="font-medium">{pace.wins.count}/{pace.wins.goal}</span>
                                          <PaceBadge status={pace.wins.paceStatus} />
                                        </li>
                                      )}
                                    </ul>
                                  ) : (
                                    <Skeleton className="h-16 w-full" />
                                  )}
                                </div>
                              </div>
                              <RepSliceDrillDown
                                userId={rep.userId}
                                range={range}
                                fullName={rep.fullName}
                              />
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
              {pipelineLoading && (
                <div className="p-4 flex justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Talent rollup — talent department only */}
          {isTalentDept && (
            <Card data-testid="card-talent-utilization">
              <CardHeader className="flex-row items-center justify-between space-y-0">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Mic2 className="h-5 w-5" /> Talent utilization ({range})
                </CardTitle>
                <Link href={`/talent?${teamQS}`}>
                  <Button variant="link" size="sm" data-testid="link-talent-all">
                    Team talent <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-muted-foreground border-b bg-muted/40">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Talent</th>
                      <th className="text-right px-3 py-2 font-medium">Bookings</th>
                      <th className="text-right px-3 py-2 font-medium">$ booked</th>
                      <th className="text-right px-3 py-2 font-medium">Upcoming (30d)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(talentUtil?.rows || []).map((t) => (
                      <tr
                        key={t.talentId}
                        className="border-b"
                        data-testid={`row-talent-${t.talentId}`}
                      >
                        <td className="px-4 py-2 font-medium">{t.name}</td>
                        <td className="text-right px-3 py-2 tabular-nums">
                          {t.invoiceCount}
                        </td>
                        <td className="text-right px-3 py-2 tabular-nums">
                          {formatMoney(t.dollarsBooked)}
                        </td>
                        <td className="text-right px-3 py-2 tabular-nums">
                          {t.upcomingShowsNext30Days}
                        </td>
                      </tr>
                    ))}
                    {talentUtil && talentUtil.rows.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">
                          No talent activity for your team in this period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
